import { z } from 'zod';
import { AgentTemplateKind, agentTemplateKindSchema, MAX_ACTIVE_TOOL_DEFINITIONS_PER_PARTNER, } from './agents.js';
export var AgentComponentType;
(function (AgentComponentType) {
    /** Instructions the agent follows in every conversation, such as tone or rules. */
    AgentComponentType["GUIDANCE"] = "guidance";
    /** Instructions the agent follows when its trigger applies, optionally calling tools. */
    AgentComponentType["SKILL"] = "skill";
    AgentComponentType["ESCALATION"] = "escalation";
})(AgentComponentType || (AgentComponentType = {}));
export var AgentComponentMode;
(function (AgentComponentMode) {
    /** Always used by the agent and never shown to tenants. */
    AgentComponentMode["HIDDEN"] = "hidden";
    /** Always used by the agent; tenants see it but cannot turn it off. */
    AgentComponentMode["ALWAYS_ON"] = "always_on";
    /** Used unless a tenant turns it off; tenants see it switched on to start. */
    AgentComponentMode["DEFAULT_ON"] = "default_on";
    /** Unused unless a tenant turns it on; tenants see it switched off to start. */
    AgentComponentMode["DEFAULT_OFF"] = "default_off";
})(AgentComponentMode || (AgentComponentMode = {}));
export const MAX_AGENT_COMPONENT_NAME_LENGTH = 120;
export const MAX_AGENT_COMPONENT_DESCRIPTION_LENGTH = 200;
export const MAX_AGENT_COMPONENT_TENANT_NAME_LENGTH = 120;
export const MAX_AGENT_COMPONENT_TENANT_DESCRIPTION_LENGTH = 300;
export const DEFAULT_AGENT_COMPONENT_PAGE_SIZE = 50;
export const MAX_AGENT_COMPONENT_PAGE_SIZE = 100;
export const MAX_SKILLS_PER_AGENT_TEMPLATE = 10;
export const componentIdSchema = z.string().uuid();
export const templateKindsUsingComponentSchema = z.array(agentTemplateKindSchema);
export const componentTypeFitsTemplate = (type, templateKind) => {
    const componentIsEscalation = type === AgentComponentType.ESCALATION;
    const templateIsVoice = templateKind === AgentTemplateKind.VOICE;
    return !componentIsEscalation || templateIsVoice;
};
export const componentTypeUsesTools = (type) => type !== AgentComponentType.ESCALATION;
const agentComponentFieldsSchema = z.object({
    name: z.string().trim().min(1).max(MAX_AGENT_COMPONENT_NAME_LENGTH),
    description: z.string().trim().max(MAX_AGENT_COMPONENT_DESCRIPTION_LENGTH),
    instructions: z.string().trim().min(1),
    mode: z.nativeEnum(AgentComponentMode),
    toolDefinitionIds: z
        .array(z.string().uuid())
        .max(MAX_ACTIVE_TOOL_DEFINITIONS_PER_PARTNER)
        .refine(toolIds => new Set(toolIds).size === toolIds.length, 'A tool can be added only once'),
    tenantName: z.string().trim().max(MAX_AGENT_COMPONENT_TENANT_NAME_LENGTH).nullable(),
    tenantDescription: z.string().trim().max(MAX_AGENT_COMPONENT_TENANT_DESCRIPTION_LENGTH).nullable(),
});
const guidanceBodySchema = agentComponentFieldsSchema.extend({
    type: z.literal(AgentComponentType.GUIDANCE),
});
const skillBodySchema = agentComponentFieldsSchema.extend({
    type: z.literal(AgentComponentType.SKILL),
    trigger: z.string().trim().min(1),
});
export const ESCALATION_MODES = [
    AgentComponentMode.ALWAYS_ON,
    AgentComponentMode.DEFAULT_ON,
    AgentComponentMode.DEFAULT_OFF,
];
const escalationBodySchema = agentComponentFieldsSchema
    .omit({ toolDefinitionIds: true })
    .extend({
    type: z.literal(AgentComponentType.ESCALATION),
})
    .strict();
const componentIsHidden = (body) => body.mode === AgentComponentMode.HIDDEN;
const componentIsHiddenEscalation = (body) => body.type === AgentComponentType.ESCALATION && componentIsHidden(body);
export const agentComponentBodySchema = z
    .discriminatedUnion('type', [guidanceBodySchema, skillBodySchema, escalationBodySchema])
    .refine(body => !componentIsHiddenEscalation(body), {
    path: ['mode'],
    message: "Tenants see every escalation, so it can't be hidden",
})
    .refine(body => componentIsHidden(body) || Boolean(body.tenantName), {
    path: ['tenantName'],
    message: 'Tenants see this component, so it needs a tenant display name',
})
    .refine(body => componentIsHidden(body) || Boolean(body.tenantDescription), {
    path: ['tenantDescription'],
    message: 'Tenants see this component, so it needs a tenant description',
});
const savedComponentShape = {
    id: componentIdSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
};
export const agentComponentSchema = z.discriminatedUnion('type', [
    guidanceBodySchema.extend(savedComponentShape),
    skillBodySchema.extend(savedComponentShape),
    escalationBodySchema.extend(savedComponentShape),
]);
const listedComponentShape = {
    ...savedComponentShape,
    templateKinds: templateKindsUsingComponentSchema,
};
export const agentComponentListItemSchema = z.discriminatedUnion('type', [
    guidanceBodySchema.extend(listedComponentShape),
    skillBodySchema.extend(listedComponentShape),
    escalationBodySchema.extend(listedComponentShape),
]);
/** One of the partner's active tools, as a component's tool picker lists it. */
export const agentComponentToolOptionSchema = z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    /** Null for a tool that looks up synced data instead of calling the partner's API. */
    http: z.object({ method: z.string(), path: z.string() }).nullable(),
    componentCount: z.number().int(),
});
export const componentUsingToolSchema = z.object({
    componentId: componentIdSchema,
    name: z.string(),
    type: z.nativeEnum(AgentComponentType),
});
export const componentsUsingToolSchema = z.object({
    published: z.array(componentUsingToolSchema),
    draftOnly: z.array(componentUsingToolSchema),
});
/** Lists one type of component, alphabetically by name; the cursor is the last component of the previous page. */
export const agentComponentListQuerySchema = z.object({
    type: z.nativeEnum(AgentComponentType),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(MAX_AGENT_COMPONENT_PAGE_SIZE)
        .default(DEFAULT_AGENT_COMPONENT_PAGE_SIZE),
    cursor: z.string().uuid().optional(),
});
export const agentComponentPageSchema = z.object({
    results: z.array(agentComponentListItemSchema),
    pagination: z.object({
        hasNextPage: z.boolean(),
        cursor: z.string().uuid().nullable(),
        total: z.number().int(),
    }),
});
export var AgentComponentField;
(function (AgentComponentField) {
    AgentComponentField["MODE"] = "mode";
    AgentComponentField["INSTRUCTIONS"] = "instructions";
    AgentComponentField["TRIGGER"] = "trigger";
    AgentComponentField["TOOLS"] = "toolDefinitionIds";
    AgentComponentField["TENANT_NAME"] = "tenantName";
    AgentComponentField["TENANT_DESCRIPTION"] = "tenantDescription";
})(AgentComponentField || (AgentComponentField = {}));
export var AgentComponentChangeType;
(function (AgentComponentChangeType) {
    AgentComponentChangeType["ADDED"] = "added";
    AgentComponentChangeType["REMOVED"] = "removed";
    AgentComponentChangeType["EDITED"] = "edited";
})(AgentComponentChangeType || (AgentComponentChangeType = {}));
const tenantComponentChoiceSchema = z.object({
    enabled: z.boolean(),
});
export const tenantComponentChoicesBodySchema = z.object({
    choices: z.record(componentIdSchema, tenantComponentChoiceSchema),
});
export const tenantFacingComponentSchema = z.object({
    componentId: componentIdSchema,
    type: z.nativeEnum(AgentComponentType),
    mode: z.nativeEnum(AgentComponentMode),
    tenantName: z.string().nullable(),
    tenantDescription: z.string().nullable(),
    enabled: z.boolean(),
});
export const publishedTemplateVersionSchema = z.object({
    id: z.string().uuid(),
    version: z.number().int(),
    publishedAt: z.string().datetime(),
});
export const unpublishedComponentChangeSchema = z.object({
    componentId: componentIdSchema,
    name: z.string(),
    type: z.nativeEnum(AgentComponentType),
    change: z.nativeEnum(AgentComponentChangeType),
    changedFields: z.array(z.nativeEnum(AgentComponentField)),
});
export const unpublishedToolChangeSchema = z.object({
    toolId: z.string().uuid(),
    name: z.string(),
    publishedVersion: z.number().int().nullable(),
    latestVersion: z.number().int(),
    changedFields: z.array(z.string()),
});
export const unpublishedTemplateChangesSchema = z.object({
    publishedVersion: publishedTemplateVersionSchema.nullable(),
    changes: z.array(unpublishedComponentChangeSchema),
    toolChanges: z.array(unpublishedToolChangeSchema).optional(),
    reviewFingerprint: z.string().optional(),
});
export const publishTemplateBodySchema = z
    .object({
    expectedReviewFingerprint: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .optional(),
})
    .optional()
    .default({});
//# sourceMappingURL=agent-components.js.map